// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── What the artefact is ─────────────────────────────────────────
/**
 * The kind of regulatory artefact, independent of who may see it and of whether
 * it can be filled in.
 *
 * The distinction that matters commercially is the first three against the rest:
 * an **official** artefact is one an authority published, and claiming that
 * falsely is the failure this taxonomy exists to prevent. `internal_template`
 * is the honest default, and the value **1,146** of the corpus's 1,645 forms (1,143 of 1,641 → 1,146 of 1,645 on 2026-10-31, merging `main` (PR #3210, up to 8babd2fccc) into the MST consultation draft Circular on digital platforms intake: the draft's Mẫu số 1–3 and its evidence register (vn-digital-platforms-circular-draft-2026 1.0.1): Mẫu số 1–3 declare `internal_template`, the register `evidence_artifact`, written on the branch as 1,137 of 1,635 → 1,140 of 1,639; 1,137 of 1,635 → 1,143 of 1,641 on 2026-10-31, merging `main` (PR #3200, #3204, #3205) into the draft vehicle-monitoring QCVN intake branch, measured on the merged tree: the branch's six vehicle-monitoring templates, each declaring `internal_template`, written on the branch as 1,134 of 1,632 → 1,140 of 1,638; 1,134 of 1,632 → 1,137 of 1,635 on 2026-10-31: the draft eID law pack 1.4.0's three internal readiness templates (sector scoping, factor consent and sharing, identifier reconciliation), internal_template, `lifecycle_status: draft_readiness`, each declaring `visibility: internal_only`, none restricted, legacy nor draft; 1,132 of 1,630 → 1,134 of 1,632 on 2026-10-28 (Sprint 62.49.0, S1): the Circular 63/2026/TT-BCT pack 1.1.0's two new register templates, each declaring `internal_template`; 1,130 of 1,628 → 1,132 of 1,630 on 2026-10-28, merging `main` (PR #3162–#3168) into Sprint 62.52.0: the Decision 1964/QĐ-TTg pack 1.2.0's two new register templates (the assessment catalogue and the supplier evidence request), each declaring `internal_template`, written on the branch as 1,122 of 1,620 → 1,124 of 1,622; 1,129 of 1,627 → 1,130 of 1,628 on 2026-10-27, merging `main` (PR #3161) into the Sprint 62.55.0 owner-answer branch: `main`'s Decree 386/2026/NĐ-CP Điều 49 khoản 3 template (the foreign-cooperation risk-assessment record), an internal template, measured on `main` as 1,121 of 1,619 → 1,122 of 1,620; 1,121 of 1,619 → 1,129 of 1,627 on 2026-10-27, the Law 108/2025/QH15 and Decree 252/2026/NĐ-CP intakes consolidated onto PR #3140's head: their eight internal register templates, each declaring `internal_template`; 1,111 of 1,609 → 1,121 of 1,619 on 2026-10-27, merging `main` (PR #3144, #3149, #3159) into Sprint 62.55.0: Sprint 62.55.0's ten customs tax-administration register templates (logistics overlay 1.16.0), all internal_template, measured on the branch as 1,116 of 1,493 before it; 1,111 of 1,488 → 1,111 of 1,609, re-based 2026-10-26 on the fourth merge of `main` (PR #3144) into Sprint 62.56.0: `main`'s Sprint 63.40.0 adds the Decree 133/2025/NĐ-CP pack's 121 annex forms, each reaching official_form; 1,106 of 1,483 → 1,111 of 1,488, re-based 2026-10-25 on the second merge of `main` (PR #3124, #3135) into Sprint 62.56.0: the branch's five biometric templates (vn-identity-biometrics-qcvn-16-17-2026 1.2.0 and 1.3.0: two readiness reviews, the voice deletion record and two supervision-act templates), each declaring `internal_template`; the branch measured 1,111 of 1,416 before it; 1,481 → 1,483 on 2026-10-25, merging `main` (PR #3126) into Sprint 63.33.0: the Circular 55/2026/TT-NHNN pack 1.3.1's two internal templates (the written request and the change proposal), declaring `internal_template`; 1,104 of 1,481 after the fourth merge of `main` (PR #3123) adds the Circulars 169/2026/TT-BCA and 170/2026/TT-BCA pack's three internal templates; 1,101 of 1,478 after the second merge of `main` (PR #3119) adds the 76 Phụ lục I forms of the Circular 86/2026/TT-BTC intake, which reach official_form; 1,101 of 1,402 measured on the merged tree, 2026-10-24, merging `main` into Sprint 63.33.0. On the branch: 1,079 of 1,380 since 2026-10-19, when the ten Circular 34/2026/TT-BCT annex forms were seeded as official forms; 1,370 earlier the same day, when 18 more official annex forms were seeded; 1,352 since 2026-10-18, when 44 official annex
 * forms were seeded and this count did not move. On `main`: 1,100 of 1,329 → 1,101 of 1,330 on 2026-10-23, Sprint 62.50.0, the Circular 18/2025/TT-NHNN pack's fifth internal template, the connection-readiness record; 1,098 of 1,327 → 1,100 of 1,329 on 2026-10-23, on merging `main`, the Circular 152/2026/TT-BTC pack's two internal templates, written on `main` as 1,081 of 1,310 → 1,083 of 1,312; 1,094 of 1,323 → 1,098 of 1,327 on 2026-10-22, the Circular 18/2025/TT-NHNN pack's four internal templates; 1,085 of 1,314 → 1,094 of 1,323 on 2026-10-22, the Decree 386/2026/NĐ-CP pack's seven and the Decision 1964/QĐ-TTg pack's two internal templates; 1,081 of 1,310 → 1,085 of 1,314 the same day, the Circular 55/2026/TT-NHNN pack's four internal templates; 1,079 of 1,308 → 1,081 of 1,310 on 2026-10-18,
 * vn-pdpl 1.64.0: two internal companion records; 1,079 of 1,308 measured on the merge of `main`, 2026-10-12: the
 * base was 1,069 of 1,296, the branch added three resolving templates to reach 1,072 of 1,299 and `main` three to reach 1,072 of
 * 1,301, so the merged corpus carries 1,069 + 3 + 3 = 1,075 of 1,304; 1,070 → 1,072 and 1,297 → 1,299 on 2026-10-11, close-out fix B2: the controlled-testing templates and the 1.0.10 release's two forms; previous values 1,070 and 1,297)
 * resolve to once `classifyTemplate` has run — derived at migration time, never
 * stored, so it is re-derived rather than read.
 */
export const REGULATORY_ARTIFACT_KINDS = [
    /** An annexed form an instrument publishes for completion and return. */
    'official_form',
    /** An authority-published procedure or process description. */
    'official_procedure',
    /** An enumerated list an authority publishes — categories, thresholds, codes. */
    'official_list',
    /** An authority-published reference that is neither a form nor a list. */
    'official_reference',
    /** A form on an authority's portal or admin system, not annexed to an instrument. */
    'portal_artifact',
    /** Authored by us. Not published by any authority, and must never claim to be. */
    'internal_template',
    /** A record an organisation produces as evidence, not a form it returns. */
    'evidence_artifact',
    /** Explanatory or methodological content. Never evidence of compliance. */
    'guidance',
];
/**
 * The kinds that may carry an "official" badge.
 *
 * A set rather than a prefix test on the string. `official_form`.startsWith
 * would work today and would silently admit a future `official_looking_thing`;
 * more importantly, a reader of this file should be able to see the list.
 */
export const OFFICIAL_ARTIFACT_KINDS = [
    'official_form',
    'official_procedure',
    'official_list',
    'official_reference',
];
/** Kinds that are read, never filled in. AC3: they must not render as empty forms. */
export const NON_FILLABLE_ARTIFACT_KINDS = [
    'official_list',
    'official_reference',
    'guidance',
];
// ── Who may see it ───────────────────────────────────────────────
/**
 * The access tier, independent of kind.
 *
 * Deliberately three values and not four: `normal` and `internal` are both
 * *discoverable* tiers differing only in which pickers surface them, and that
 * distinction already lives in `FormTemplate.visibility`, which is unchanged by
 * this sprint. What is new is the line between "discoverable at all" and
 * "requires a positive grant", because that line cannot be drawn by exclusion.
 */
export const ARTIFACT_ACCESS_TIERS = [
    /** Discoverable by anyone with read permission. `visibility` still applies. */
    'open',
    /** Discoverable, but only to holders of the artefact's declared permission. */
    'controlled',
    /**
     * Not discoverable without an active grant. Absent from list, search, count,
     * detail and export for anyone without one, and every attempt is recorded.
     */
    'restricted',
];
/**
 * Why a session may or may not see an artefact.
 *
 * Returned by one resolver that every read path calls. The reasons are
 * enumerated rather than free text because a denial is audited, and an audit row
 * whose reason is a sentence cannot be counted, filtered or reported on.
 */
export const ARTIFACT_ACCESS_REASONS = [
    /** The artefact is `open` and the session holds read permission. */
    'open_tier',
    /** The session holds the permission the artefact's tier requires. */
    'permission_held',
    /** An active, unexpired grant names this session and this artefact. */
    'active_grant',
    /** No grant, and the tier requires one. */
    'no_grant',
    /** A grant exists and its window has closed. */
    'grant_expired',
    /** A grant existed and was revoked. */
    'grant_revoked',
    /** The tier requires a permission the session does not hold. */
    'permission_missing',
    /**
     * The artefact declares a tier the organisation's policy does not permit at
     * all — e.g. a tenant that has not enabled restricted handling. Distinct from
     * `no_grant`: no grant could be issued, so directing the operator to request
     * one would send them somewhere that cannot help.
     */
    'policy_forbids',
];
// ── The grant ────────────────────────────────────────────────────
export const ARTIFACT_GRANT_STATUSES = ['active', 'expired', 'revoked'];
/**
 * What the actor **did**, never what the platform answered.
 *
 * The distinction is load-bearing and it was got wrong. `requestAccess` used to
 * write `kind: decision.allowed ? 'viewed' : 'denied'` — deriving the *act* from
 * the *outcome* — so an access request and a stale link both landed as `denied`
 * and the custodian's register could not tell "someone is asking me for access"
 * from "someone hit a dead link". The refusals KPI counted them together.
 *
 * `requested` fixes that at the root rather than at the reader. A request is a
 * request whether or not the requester turns out to already hold access, so it
 * is written unconditionally; the platform's answer at that moment lives in
 * `decision`, which is where an outcome belongs.
 *
 * The alternative considered was leaving the kinds alone and filtering on
 * `context` — the route string `POST /regulatory-artifacts/:id/access-request`.
 * Rejected: `context` is *provenance*, recording which route produced a row so a
 * denial can be traced, and `getById` reaches it from three different contexts
 * that are all the same act. Making a route string the taxonomy would mean a
 * rename silently reclassifies history — the filter stops matching and the count
 * drops to zero rather than erroring — and would keep two different acts as one
 * act with a naming convention on top, which is the finding restated rather than
 * closed. As a kind it is queryable through the existing `kind` parameter, with
 * no new API surface and a server-side `total` the custodian can trust.
 */
export const ARTIFACT_ACCESS_EVENT_KINDS = [
    'viewed',
    'downloaded',
    'exported',
    'denied',
    /** Someone stated in writing why they need an artefact they cannot reach. */
    'requested',
    /**
     * Sprint 64.148.0 — the artefact was OBTAINED by the organisation, or REMOVED
     * from it. Two acts, one kind, because both are movements of the record
     * itself rather than reads of its content.
     *
     * A separate member because the five above are all readings, and recording a
     * deletion as `viewed` would put a false statement on a trail whose whole
     * value is that a custodian can trust it. Under this model uninstalling a
     * classified register is the strongest access there is — it does not read the
     * record, it ends it — and a taxonomy that cannot say so cannot answer the
     * question an inspection actually asks.
     *
     * **These apply to the ALLOWED branch only, and a refusal stays `denied`.**
     *
     * Gate E12.5 corrected this docblock, which had said the opposite — that a
     * refused install records `installed` + a denied decision. It does not, and
     * the reason is load-bearing rather than an implementation shortcut: the
     * custodian's Refusals KPI counts `kind: 'denied'` and nothing else, so a
     * refused install recorded as `installed` would be a refusal that the one
     * surface built to show refusals cannot see. That is Sprint 64.27.0's own
     * gate finding, and stating the reverse here — in the file a future
     * implementer reads first — would have invited someone to "fix" the code to
     * match the comment.
     *
     * "Who tried to bring one of these into the org" stays answerable from
     * `context`, which names the route on both branches.
     */
    'installed',
    'uninstalled',
];
// ── The artefact record ──────────────────────────────────────────
export const ARTIFACT_VERIFICATION_STATUSES = [
    /** Classified and provenance confirmed by a person. */
    'verified',
    /** Classified by migration from existing content; awaiting review. */
    'migrated_unreviewed',
    /**
     * The migration could not classify it. Deliberately not a guess — AC8 forbids
     * inventing official facts, and an unclassified artefact is blocked from
     * official badges until a person decides.
     */
    'unclassified',
];
/**
 * Why a record is in the verification queue, one code per distinct condition.
 *
 * Four of the five are the **classifier's** refusals — the migration read the
 * record, could not say what it is, and said why. The fifth is not a refusal at
 * all: `pack_declaration_diverged` is recorded against a record a *person* has
 * classified, when the content installed afterwards declares a different access
 * posture from the one stored. See `syncArtifactsForOrg`, which is the only
 * writer of it: the person's classification is left exactly as they made it, and
 * the disagreement is put on the record instead of being dropped. A silently
 * dropped tier escalation is the one outcome this registry must not produce.
 *
 * ## Why this is here rather than in the migration that produces it
 *
 * It was declared in `migrations/regulatory-artifacts-init.ts`, and its values
 * never reached a reader: `noteFor()` flattened the code, the candidate kind and
 * the provenance gaps into an **English sentence** at write time, and
 * `RegulatoryArtifact` kept only `verification_note: string | null`. The
 * Verification Queue's note column — the column the page's own copy calls the
 * point of the queue — therefore rendered English in every locale and
 * interpolated raw enum identifiers into it (`official_form`,
 * `provenance.source_instrument`). `classifyTemplate` computed all three values
 * and discarded two.
 *
 * A code is a fact the platform records; the words are the interface's, in the
 * reader's language. So the union lives beside the contract that carries it,
 * both layers obtain the same one, and **nothing translates in the backend** —
 * which is the whole point of the change and the reason a helper that returns a
 * ready-made sentence would undo it.
 *
 * Enumerated rather than free text for the same reason the access reasons are:
 * the queue is counted and filtered, and a reason that is a sentence cannot be.
 */
export const UNCLASSIFIED_REASONS = [
    /** The record claims to be official and cannot support the claim. */
    'official_claimed_without_provenance',
    /** An official form number, and no statement either way that it is official. */
    'official_form_id_without_official_flag',
    /** `artifact_classification` holds a value that is not a known kind. */
    'unrecognised_classification_value',
    /** Nothing in the record says what it is. */
    'no_classification_signal',
    /**
     * A person classified this record, and the content installed since declares a
     * different access tier — or a different permission for it — from the one
     * stored. **Not a classifier refusal**: nothing here overwrites the person's
     * decision, and the code exists so the disagreement is visible to the next
     * custodian rather than resolved silently in either direction.
     */
    'pack_declaration_diverged',
];
// ── Disposition: is this a person's decision, or an install that fell behind? ──
/**
 * Sprint 64.29.0 — what a verification-queue row actually **is**, at the level a
 * person can act on.
 *
 * The queue was shipped by 62.6.0 as *"the bounded record of work still owed"*
 * and presented every row as a decision a compliance officer owes. Re-measured
 * at this sprint's kickoff, that was false of **every single unclassified row**:
 * 549 of 549 classify cleanly against the content the packs ship on disk today,
 * and **0** remain refused. The queue was not measuring ambiguity. It was
 * measuring how far behind the tenant's installed content had fallen, and
 * addressing the difference to the wrong person — a compliance officer cannot
 * resolve a stale pack install by adjudicating artefacts one at a time.
 *
 * So a row's disposition is a **claim about who can resolve it**, not a
 * restatement of `verification_status`:
 *
 * - `stale_install` — the installed template is behind the pack that ships it,
 *   and the current content *does* say what this is. Resolvable by upgrading the
 *   pack. Nobody should be asked to decide it.
 * - `genuine_ambiguity` — the content the pack ships **today** still does not say
 *   what this record is. This is a person's call, and it is the only bucket that
 *   is.
 * - `awaiting_confirmation` — classified, with a complete citation, and no person
 *   has confirmed it against the source. Not a refusal at all; it is
 *   `migrated_unreviewed`, and it is real human work of a different kind.
 * - `orphaned_template` — the `schema_id` is in no pack on disk. Not deleted (a
 *   form instance may reference it) and **not** put in front of a person: there
 *   is no current content to check it against. Counted and named instead.
 *
 * Derived at read time from the installed template and the packs on disk.
 * Deliberately **not stored**: it is a fact about the relationship between two
 * things that both change, and a stored copy would be stale in exactly the
 * situation it exists to describe.
 */
export const ARTIFACT_DISPOSITIONS = [
    'stale_install',
    'genuine_ambiguity',
    'awaiting_confirmation',
    'orphaned_template',
];
/**
 * One reason the currency check did not pass, as a code plus its identifiers.
 *
 * ## Why this is not a sentence — gate E12.5 pass 2, F3
 *
 * `failures` used to be `string[]`, built in English by the service and rendered
 * verbatim on the verification queue. The docblock authorising that said each
 * entry was "a pack code and two version strings, which are identifiers rather
 * than prose". It was not: five of the nine emitted strings contain no identifier
 * at all (*"read 0 pack manifests on disk — the currency check has no baseline"*)
 * and the rest are full English sentences with an identifier in the middle. A
 * Vietnamese administrator got English under a Vietnamese heading.
 *
 * The identifiers stay identifiers — `pack_code` and the two versions are opaque
 * strings compared byte for byte, never translated — and the **reason** is a code
 * the reading surface renders in the reader's own language.
 *
 * R-46: `code` names no framework, instrument, authority or jurisdiction. It
 * names what the check found.
 */
export const PACK_CURRENCY_FAILURE_CODES = [
    /** No `pack.json` was parsed under either pack root. */
    'no_packs_on_disk',
    /** Manifests were read and no form record was. */
    'no_forms_on_disk',
    /** The organisation has no installed templates. */
    'no_templates_read',
    /** The read covered no organisation. */
    'no_orgs_read',
    /** No installed template records which pack it came from. */
    'no_installs_read',
    /** The pack is installed at a version older than the one on disk. */
    'pack_behind',
    /** The pack is at the version on disk and its content no longer derives the same. */
    'pack_content_diverged',
    /** The pack is on disk and its manifest declares no version. */
    'pack_manifest_no_version',
    /** The pack code matched no `pack.json` under either pack root. */
    'pack_not_on_disk',
];
// ── Collections ──────────────────────────────────────────────────
export const REGULATORY_ARTIFACT_COLLECTION = 'grc_regulatory_artifacts';
export const ARTIFACT_ACCESS_GRANT_COLLECTION = 'grc_artifact_access_grants';
export const ARTIFACT_ACCESS_EVENT_COLLECTION = 'grc_artifact_access_events';
// ── Errors ───────────────────────────────────────────────────────
/**
 * One code per distinct condition, with the 62.4.0/62.5.0 rule applied from the
 * start: no code is thrown at two different statuses, because a consumer cannot
 * tell "your request was wrong" from "this tenant's content is broken" if the
 * code is the same.
 */
export const ARTIFACT_ERROR_CODES = {
    ARTIFACT_NOT_FOUND: 'regulatory_artifact_not_found',
    /** The session may not see it. A 404 to the caller — see the service docblock. */
    ACCESS_DENIED: 'regulatory_artifact_access_denied',
    GRANT_NOT_FOUND: 'artifact_access_grant_not_found',
    GRANT_ALREADY_REVOKED: 'artifact_access_grant_already_revoked',
    /** A grant was requested for an artefact whose tier needs none. */
    GRANT_NOT_APPLICABLE: 'artifact_access_grant_not_applicable',
    PURPOSE_REQUIRED: 'artifact_access_purpose_required',
    /** The requested window exceeds what policy allows. */
    WINDOW_TOO_LONG: 'artifact_access_window_too_long',
    EXPIRY_IN_PAST: 'artifact_access_expiry_in_past',
    /** An official kind was set without complete provenance. */
    PROVENANCE_INCOMPLETE: 'regulatory_artifact_provenance_incomplete',
    /** A non-fillable kind was marked fillable, or the reverse. */
    FILLABILITY_MISMATCH: 'regulatory_artifact_fillability_mismatch',
    /** A `controlled` artefact declares no required permission, or `open` declares one. */
    TIER_PERMISSION_MISMATCH: 'regulatory_artifact_tier_permission_mismatch',
    GRANTEE_NOT_FOUND: 'artifact_access_grantee_not_found',
    ORG_POLICY_FORBIDS: 'regulatory_artifact_org_policy_forbids',
};
// ── The one resolver every read path must use ────────────────────
/**
 * `true` when `a` is the same instant as `b` or later, comparing **instants**
 * rather than the strings that spell them.
 *
 * Kept here rather than in `utils/` because it exists to serve one decision and
 * its unparseable-input behaviour is chosen for that decision specifically:
 * **an input this cannot read is treated as expired.** For an access grant that
 * is the safe direction — a malformed `expires_at` denies rather than admits —
 * and it would be the wrong direction for almost any other caller, so promoting
 * it to a general date utility would hand the next consumer a fail-open helper
 * wearing a neutral name.
 */
function isAtOrAfter(a, b) {
    const ta = Date.parse(a);
    const tb = Date.parse(b);
    if (Number.isNaN(ta) || Number.isNaN(tb))
        return true;
    return ta >= tb;
}
/**
 * Decide whether a session may see an artefact.
 *
 * **Pure.** No database, no clock beyond the `now` passed in, no randomness — so
 * the same inputs give the same decision forever, and a decision recorded in an
 * audit row can be re-derived years later. That is the same property 62.4.0's
 * classification evaluator holds and for the same reason.
 *
 * It lives in shared rather than in the backend module because the frontend must
 * reach the *same* answer to decide whether to render a control. A control the
 * server will refuse is a dead control; a control hidden when the server would
 * allow is a capability nobody can reach. Two implementations would drift, and
 * the drift would look like a permissions bug.
 */
export function resolveArtifactAccess(input) {
    const none = {
        grant_id: null,
        grant_expires_at: null,
    };
    if (input.tier === 'open') {
        return { allowed: true, reason: 'open_tier', ...none };
    }
    if (input.tier === 'controlled') {
        const required = input.requiredPermission;
        // A controlled artefact with no declared permission is content that failed
        // validation. Refusing is the safe reading: the alternative is a tier that
        // silently behaves as `open`.
        if (!required || !input.sessionPermissions.includes(required)) {
            return { allowed: false, reason: 'permission_missing', ...none };
        }
        return { allowed: true, reason: 'permission_held', ...none };
    }
    // ── restricted ──
    //
    // The org policy check comes FIRST, and its own reason. If the tenant does not
    // permit restricted handling, no grant could be issued — so telling the
    // operator "no grant" would send them to request one that cannot exist.
    if (!input.orgPermitsRestricted) {
        return { allowed: false, reason: 'policy_forbids', ...none };
    }
    // A restricted artefact is subject to its declared permission as well as its
    // grant, and this check has to come BEFORE the grant checks.
    //
    // The first draft of this function omitted it entirely, which made the
    // strictest tier *weaker* than `controlled` in one dimension: a restricted
    // artefact declaring a permission had that declaration ignored, so a session
    // holding no permission at all but holding a grant was allowed through, while
    // the same session would have been refused the same artefact at `controlled`.
    // A grant answers "may this person reach this record"; the permission answers
    // "may this person reach records of this kind at all". Neither substitutes for
    // the other, and a tier that reads one as covering the other is not a tier.
    const required = input.requiredPermission;
    if (required && !input.sessionPermissions.includes(required)) {
        return { allowed: false, reason: 'permission_missing', ...none };
    }
    const grant = input.grant;
    if (!grant)
        return { allowed: false, reason: 'no_grant', ...none };
    if (grant.status === 'revoked') {
        return { allowed: false, reason: 'grant_revoked', grant_id: grant._id, grant_expires_at: null };
    }
    // Expiry is decided by the timestamp, not by the stored status. A sweeper that
    // has not run yet must not extend access — `status: 'active'` on a grant whose
    // window closed is a stale row, and reading it as authoritative is how a
    // time-bounded grant quietly becomes permanent.
    //
    // Compared as instants, not as strings. The first draft wrote
    // `grant.expires_at <= input.now`, which is only correct while both sides
    // happen to be in the identical ISO spelling. They are not guaranteed to be:
    // Mongo round-trips a date as `…:00.000Z` while a hand-written or
    // pack-supplied value is commonly `…:00Z`, and lexicographically `'Z'` (0x5A)
    // sorts *after* `'.'` (0x2E) — so a grant expiring at exactly the current
    // instant would compare as still open. An offset spelling (`+07:00`, which is
    // the local form throughout this product's home market) breaks the ordering
    // outright rather than by one edge. `isAtOrAfter` normalises both sides and
    // refuses rather than guesses when either is unparseable.
    if (grant.status === 'expired' || isAtOrAfter(input.now, grant.expires_at)) {
        return {
            allowed: false,
            reason: 'grant_expired',
            grant_id: grant._id,
            grant_expires_at: grant.expires_at,
        };
    }
    return {
        allowed: true,
        reason: 'active_grant',
        grant_id: grant._id,
        grant_expires_at: grant.expires_at,
    };
}
// ── The one badge rule every layer must obtain ───────────────────
/**
 * What an artefact's official badge may say, and why it says it.
 *
 * Ordered as a reader needs it, and the order is load-bearing — see
 * `resolveOfficialStanding`.
 */
export const OFFICIAL_STANDINGS = [
    /** An official kind, complete provenance, and a named person behind it. */
    'official',
    /** The kind admits no badge. A statement about what the thing is. */
    'not_official_kind',
    /** The kind admits a badge and the citation is missing part of itself. */
    'provenance_incomplete',
    /** The citation is complete and no person has confirmed it yet. */
    'awaiting_verification',
];
function localeValuePresent(value) {
    if (!value || typeof value !== 'object')
        return false;
    return Object.values(value).some((v) => typeof v === 'string' && v.trim() !== '');
}
function stringPresent(value) {
    return typeof value === 'string' && value.trim() !== '';
}
/** The localized source captions the standing rule requires and report projections retain. */
export const OFFICIAL_SOURCE_CAPTION_FIELDS = ['source_instrument', 'authority'];
/**
 * Whether an artefact may display as official, and — when it may not — why.
 *
 * ## Why this is here and not in either layer
 *
 * It sits beside `resolveArtifactAccess` for the reason that function's own
 * docblock gives: the interface must reach the **same** answer as the server
 * rather than a parallel one. Not "apply the same rule" — **obtain the same
 * object**. It was written because the two layers had each grown a field list
 * and the lists overlapped on two entries of seven, with the result that **no
 * artefact displayed as official in any tenant** while the audit row for the
 * same record recorded that it was eligible. A rule stated twice is a rule that
 * disagrees with itself; the only structural fix is that there is one of it.
 *
 * ## What the badge stands on, and why each decision is the way it is
 *
 * **Required — `source_instrument`, `authority`, `title.en`, `title.vi`, and
 * `appendix_or_form_number` when the kind is `official_form`.** These five are
 * the citation: which instrument published this, who published it, what it is
 * called in each language the product speaks, and — for the one kind that *is*
 * a numbered annexed form — the number as printed. A reader handed those can
 * find the source. The form number is required for `official_form` alone
 * because a published procedure, list or reference is not an annexed numbered
 * form, and demanding a number of it would make the honest classification
 * impossible.
 *
 * **Not required — `source_url`.** An instrument published in print is still
 * published. A web address is where *we* happened to read it, which is a
 * convenience for checking and not a property of the publication; requiring it
 * would deny the badge to a correctly cited artefact on the grounds that the
 * authority has no website. This is the same argument the interface already
 * made for the two below, extended to its obvious next case.
 *
 * **Not required — `instrument_version`.** An instrument that states no version
 * is still published. Requiring one would deny the badge on the grounds that
 * the publisher did not number its own edition.
 *
 * **Not required, and not provenance at all — `verified_at` / `verified_by`.**
 * These answer a different question. Provenance is *what the instrument
 * published*; verification is *whether anyone here has checked*. Folding the
 * second into the first produced the defect's most misleading symptom: an
 * artefact carrying its instrument, its authority and its form number reported
 * "provenance incomplete", which reads as a citation with a hole in it and sent
 * an operator to look for a field that was already filled in. So they move out
 * of the completeness test and into the standing, as `awaiting_verification` —
 * a real, reachable state meaning *the citation is complete and no person has
 * confirmed it yet*.
 *
 * That state is the honest one for every migrated record, and it is what the
 * verification queue exists to clear. The migration sets `verified_at` and
 * `verified_by` to `null` unconditionally, correctly: a migration must not
 * stamp a human confirmation. Before this function existed, the interface
 * required them, so `awaiting_verification` was **unreachable for exactly the
 * records it was written for**.
 *
 * `verified_by` is the one read, not `verified_at`: the fact is that a *person*
 * stood behind it. The timestamp is stamped alongside by the server and cannot
 * be supplied without them, so testing both would test one thing twice while
 * suggesting a record could carry a time with nobody attached to it.
 *
 * ## The order
 *
 * `not_official_kind` → `provenance_incomplete` → `awaiting_verification` →
 * `official`, and each step settles the question the next one presupposes.
 * "This kind carries no badge" is a statement about what the thing *is*; only
 * once the kind admits a badge does the citation matter; and only once the
 * citation is complete does it matter that nobody has checked it. Reversed at
 * the first step, an internal template reads as "provenance incomplete", which
 * is a gap to fill rather than a correct classification. Reversed at the
 * second, an incomplete citation reads as merely unverified, which sends the
 * reader to the wrong queue.
 *
 * **Pure.** No database, no clock, no randomness — the same artefact resolves
 * the same way forever, so a standing recorded in an audit row can be
 * re-derived years later.
 */
export function resolveOfficialStanding(artifact) {
    if (!OFFICIAL_ARTIFACT_KINDS.includes(artifact.kind)) {
        return { standing: 'not_official_kind', gaps: [] };
    }
    const p = artifact.provenance;
    if (!p)
        return { standing: 'provenance_incomplete', gaps: ['provenance'] };
    const gaps = [];
    for (const field of OFFICIAL_SOURCE_CAPTION_FIELDS) {
        if (!localeValuePresent(p[field]))
            gaps.push(`provenance.${field}`);
    }
    if (!stringPresent(artifact.title?.en))
        gaps.push('title.en');
    if (!stringPresent(artifact.title?.vi))
        gaps.push('title.vi');
    if (artifact.kind === 'official_form' && !stringPresent(p.appendix_or_form_number)) {
        gaps.push('provenance.appendix_or_form_number');
    }
    if (gaps.length > 0)
        return { standing: 'provenance_incomplete', gaps };
    // A record the migration could not classify has had no person stand behind
    // it, so it is awaiting verification for the same reason a citation nobody
    // has confirmed is — the two conditions are one state, not two.
    if (artifact.verification_status === 'unclassified' || !stringPresent(p.verified_by)) {
        return { standing: 'awaiting_verification', gaps: [] };
    }
    return { standing: 'official', gaps: [] };
}
//# sourceMappingURL=regulatory-artifact.js.map